import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSocket } from '../../../context/SocketContext';
import { USER_ROLES } from '../../../utils/constants';
import SidebarItem from './SidebarItem';
import SidebarSection from './SidebarSection';
import SidebarGroup from './SidebarGroup';
import SidebarFooter from './SidebarFooter';
import SearchMenu from './SearchMenu';
import { allNavigationItems } from '../../../config/navigationItems';
import { hasPermission as checkPermission, hasHRMSPermission, hasCRMPermission } from '../../../utils/navigationUtils';

const DynamicSidebar = ({
  userRole,
  user,
  onNavigate,
  activeItem,
  onLogout,
  isCollapsed = false,
  expandedItems,
  onToggleExpanded,
  searchTerm = ''
}) => {
  const [forceUpdate, setForceUpdate] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const socket = useSocket();

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || `${window.location.origin.replace(':3000', ':2222')}/api`;

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        const response = await axios.get(`${apiBaseUrl}/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.status === 'success') {
          setUnreadCount(response.data.data.filter(n => !n.isRead).length);
        }
      } catch (error) { }
    };

    fetchUnreadCount();

    if (socket) {
      const handleNewNotification = () => {
        setUnreadCount(prev => prev + 1);
      };
      const handleNotificationRead = () => {
        setUnreadCount(prev => Math.max(0, prev - 1));
      };
      const handleAllNotificationsRead = () => {
        setUnreadCount(0);
      };

      socket.on('new-notification', handleNewNotification);
      socket.on('notification-read', handleNotificationRead);
      socket.on('notifications-all-read', handleAllNotificationsRead);

      return () => {
        socket.off('new-notification', handleNewNotification);
        socket.off('notification-read', handleNotificationRead);
        socket.off('notifications-all-read', handleAllNotificationsRead);
      };
    }
  }, [socket, apiBaseUrl]);

  // Get user with permissions
  const getUserWithPermissions = () => {
    if (user && user.granularPermissions) {
      return user;
    }
    try {
      const userData = localStorage.getItem('user_data');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        if (parsedUser && parsedUser.granularPermissions) {
          return parsedUser;
        }
      }
    } catch (e) { }
    return user;
  };

  const userWithPermissions = getUserWithPermissions();

  const hasPermission = (permissionKey) => {
    return checkPermission(userWithPermissions, userRole, permissionKey);
  };

  const hrmsPermissionCheck = () => {
    return hasHRMSPermission(userWithPermissions, userRole);
  };

  const crmPermissionCheck = () => {
    return hasCRMPermission(userWithPermissions, userRole);
  };

  const systemPermissionCheck = () => {
    return hrmsPermissionCheck() || crmPermissionCheck();
  };

  const employeeCheck = () => {
    return userRole !== USER_ROLES.SUPER_ADMIN;
  };

  const gridMenuItemIds = new Set([
    'my_wfh_requests',
    'my_interviews',
    'background_verification',
    'my_performance',
    'policies',
    'submit_resignation'
  ]);

  const toggleExpanded = (itemId) => {
    onToggleExpanded(itemId);
  };

  useEffect(() => {
    setForceUpdate(prev => prev + 1);
  }, [activeItem]);

  // Filter sub-items based on permissions
  const getVisibleSubItems = (subItems, parentItem = null) => {
    if (!subItems) return [];
    return Object.values(subItems).filter(subItem => {
      if (parentItem?.id === 'employee_self_service') {
        if (employeeCheck() && gridMenuItemIds.has(subItem.id)) {
          return false;
        }
        if (Array.isArray(subItem.roles) && subItem.roles.length > 0) {
          return subItem.roles.includes(userRole);
        }
        return true;
      }

      if (subItem.id === 'attendance_dashboard' && userRole === USER_ROLES.SUPER_ADMIN) {
        return false;
      }

      if (subItem.permissionKey) {
        return hasPermission(subItem.permissionKey);
      }

      if (Array.isArray(subItem.roles) && subItem.roles.length > 0) {
        return subItem.roles.includes(userRole);
      }

      return true;
    });
  };

  // Filter nested sub-items (3-level navigation)
  const getVisibleNestedSubItems = (nestedSubItems, parentSubItem = null) => {
    if (!nestedSubItems) return [];

    if (parentSubItem) {
      if (parentSubItem.permissionKey && hasPermission(parentSubItem.permissionKey)) {
        return Object.values(nestedSubItems).filter(nestedSubItem => {
          if (Array.isArray(nestedSubItem.roles) && nestedSubItem.roles.length > 0) {
            return nestedSubItem.roles.includes(userRole);
          }
          return true;
        });
      }
    }

    return Object.values(nestedSubItems).filter(nestedSubItem => {
      if (nestedSubItem.permissionKey) {
        return hasPermission(nestedSubItem.permissionKey);
      }
      return false;
    });
  };

  // Render a single navigation item (with potential sub-items)
  const renderItem = (item, level = 0) => {
    const visibleChildren = item.hasSubItems ? getVisibleSubItems(item.subItems, item) : [];
    
    // Search filtering
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const matchesSelf = item.label.toLowerCase().includes(term);
      const matchesChild = visibleChildren.some(child => {
        if (child.label.toLowerCase().includes(term)) return true;
        const visibleNested = child.hasSubItems ? getVisibleNestedSubItems(child.subItems, child) : [];
        return visibleNested.some(nested => nested.label.toLowerCase().includes(term));
      });

      if (!matchesSelf && !matchesChild) return null;
    }

    const isExpanded = searchTerm.trim() ? true : expandedItems[item.id];
    const isActive = activeItem === item.id ||
      visibleChildren.some(subItem =>
        activeItem === subItem.id ||
        (subItem.subItems && getVisibleNestedSubItems(subItem.subItems, subItem).some(nestedSubItem =>
          activeItem === nestedSubItem.id
        ))
      );

    const getNavigationTarget = (subItem) => {
      if (subItem.id === 'audit_logs' && item.id !== 'compliance_policy') {
        return 'analytics_audit_logs';
      }
      return subItem.id;
    };

    return (
      <div key={item.id} className="w-full">
        <SidebarItem
          key={`${item.id}-${forceUpdate}`}
          icon={item.icon}
          label={item.label}
          isActive={isActive}
          isCollapsed={isCollapsed}
          hasSubItems={item.hasSubItems && visibleChildren.length > 0}
          isExpanded={isExpanded}
          level={level}
          badge={item.id === 'notifications' && unreadCount > 0 ? unreadCount : null}
          onToggle={() => toggleExpanded(item.id)}
          onClick={() => onNavigate(item.id)}
        />

        {item.hasSubItems && visibleChildren.length > 0 && isExpanded && !isCollapsed && (
          <SidebarGroup isExpanded={isExpanded} isCollapsed={isCollapsed} level={level + 1}>
            {visibleChildren.map(subItem => {
              const visibleNested = subItem.hasSubItems ? getVisibleNestedSubItems(subItem.subItems, subItem) : [];
              
              if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase();
                const matchesSelf = subItem.label.toLowerCase().includes(term);
                const matchesNested = visibleNested.some(n => n.label.toLowerCase().includes(term));
                if (!matchesSelf && !matchesNested && !item.label.toLowerCase().includes(term)) {
                  return null;
                }
              }

              const isSubItemExpanded = searchTerm.trim() ? true : expandedItems[subItem.id];
              const isSubItemActive = activeItem === subItem.id ||
                visibleNested.some(nestedSubItem => activeItem === nestedSubItem.id);

              return (
                <div key={subItem.id} className="w-full">
                  <SidebarItem
                    key={`${subItem.id}-${forceUpdate}`}
                    icon={subItem.icon}
                    label={subItem.label}
                    isActive={isSubItemActive}
                    isCollapsed={isCollapsed}
                    hasSubItems={subItem.hasSubItems && visibleNested.length > 0}
                    isExpanded={isSubItemExpanded}
                    level={level + 1}
                    onToggle={() => toggleExpanded(subItem.id)}
                    onClick={() => {
                      if (getNavigationTarget(subItem) === 'candidate_profile_management' ||
                        getNavigationTarget(subItem) === 'offer_communication_logs') {
                        sessionStorage.setItem('navContext', item.id);
                      }
                      onNavigate(getNavigationTarget(subItem));
                    }}
                  />

                  {subItem.hasSubItems && visibleNested.length > 0 && isSubItemExpanded && (
                    <SidebarGroup isExpanded={isSubItemExpanded} isCollapsed={isCollapsed} level={level + 2}>
                      {visibleNested.map(nestedSubItem => {
                        if (searchTerm.trim()) {
                          const term = searchTerm.toLowerCase();
                          const matchesNested = nestedSubItem.label.toLowerCase().includes(term);
                          if (!matchesNested && !subItem.label.toLowerCase().includes(term) && !item.label.toLowerCase().includes(term)) {
                            return null;
                          }
                        }

                        return (
                          <SidebarItem
                            key={`${nestedSubItem.id}-${forceUpdate}`}
                            icon={nestedSubItem.icon}
                            label={nestedSubItem.label}
                            isActive={activeItem === nestedSubItem.id}
                            isCollapsed={isCollapsed}
                            level={level + 2}
                            onClick={() => onNavigate(nestedSubItem.id)}
                          />
                        );
                      })}
                    </SidebarGroup>
                  )}
                </div>
              );
            })}
          </SidebarGroup>
        )}
      </div>
    );
  };

  // Section categorization
  const dashboardItem = (hrmsPermissionCheck() || crmPermissionCheck() || hasPermission('dashboard'))
    ? allNavigationItems.dashboard
    : null;

  const hrmsItems = useMemo(() => {
    if (!hrmsPermissionCheck()) return [];
    const sub = allNavigationItems.hrms.subItems;
    if (!sub) return [];
    return Object.values(sub).filter(item => {
      if (item.permissionKey) return hasPermission(item.permissionKey);
      if (Array.isArray(item.roles) && item.roles.length > 0) return item.roles.includes(userRole);
      return true;
    });
  }, [userWithPermissions, userRole]);

  const crmItems = useMemo(() => {
    if (!crmPermissionCheck()) return [];
    const sub = allNavigationItems.crm_sales.subItems;
    if (!sub) return [];
    return Object.values(sub).filter(item => {
      if (item.permissionKey) return hasPermission(item.permissionKey);
      if (Array.isArray(item.roles) && item.roles.length > 0) return item.roles.includes(userRole);
      return true;
    });
  }, [userWithPermissions, userRole]);

  const systemItems = useMemo(() => {
    if (!systemPermissionCheck()) return [];
    const items = [];

    // Roles & Permissions is handled inside System Management -> HRMS

    // System Settings / Configuration
    if (systemPermissionCheck()) {
      items.push({
        id: 'system_management',
        label: 'System Management',
        icon: allNavigationItems.system_management?.icon,
        hasSubItems: true,
        subItems: allNavigationItems.system_management?.subItems
      });
    }

    // Notifications item is now rendered at the bottom

    return items;
  }, [userWithPermissions, userRole]);

  const employeeSelfServiceItem = allNavigationItems.employee_self_service;

  return (
    <div className="h-full flex flex-col overflow-hidden bg-white sidebar-container select-none">
      {/* Top Logo Area (Clean Mobiloitte Logo) */}
      <div className={`flex-shrink-0 border-b border-[#EEF2F7] h-[56px] flex items-center ${isCollapsed ? 'px-2 justify-center' : 'px-3.5'}`}>
        <div className="flex items-center gap-2.5">
          {/* Emblem: Blue "M" Logo */}
          <div className="w-[34px] h-[34px] flex items-center justify-center flex-shrink-0">
            <img src="/mobiloitte-logo.png" alt="Mobiloitte Logo" className="w-full h-full object-contain" onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/logo.png';
            }} />
          </div>
          {!isCollapsed && (
            <div className="min-w-0 flex flex-col justify-center">
              <h1 className="text-[17px] font-bold text-[#0F2942] leading-none tracking-tight">
                Mobiloitte
              </h1>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Content */}
      <div className={`flex-1 overflow-y-auto sidebar-scroll overflow-x-hidden ${isCollapsed ? 'p-2' : 'px-3 py-2'}`}>
        {/* Dashboard Item */}
        {dashboardItem && (
          <div className="my-1">
            {renderItem(dashboardItem)}
          </div>
        )}

        {/* HRMS Parent */}
        {hrmsItems.length > 0 && (
          <div className="my-1">
            {renderItem({
              ...allNavigationItems.hrms,
              subItems: hrmsItems.reduce((acc, item) => ({ ...acc, [item.id]: item }), {})
            })}
          </div>
        )}

        {/* CRM / SALES Parent */}
        {crmItems.length > 0 && (
          <div className="my-1">
            {renderItem({
              ...allNavigationItems.crm_sales,
              subItems: crmItems.reduce((acc, item) => ({ ...acc, [item.id]: item }), {})
            })}
          </div>
        )}

        {/* SYSTEM Items */}
        {systemItems.length > 0 && (
          <div className="space-y-1 my-1">
            {systemItems.map(item => renderItem(item))}
          </div>
        )}

        {/* EMPLOYEE Parent */}
        {employeeSelfServiceItem && (
          <div className="my-1">
            {renderItem(employeeSelfServiceItem)}
          </div>
        )}

        {/* Notifications Item */}
        {!employeeCheck() && (
          <div className="my-1">
            {renderItem(allNavigationItems.notifications)}
          </div>
        )}
      </div>

      {/* Sticky Bottom Footer */}
      <SidebarFooter
        userRole={userRole}
        user={user}
        isCollapsed={isCollapsed}
        activeItem={activeItem}
        onNavigate={onNavigate}
        onLogout={onLogout}
      />
    </div>
  );
};

export default DynamicSidebar;
